import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { asUsfOrZero, numberWithCommas } from 'spw-shared/utils';
import { MACRO_PROGRAMMING_TYPE, TOTAL_PROGRAM_TYPE } from 'spw-constants';
import { currentProject } from 'spw-redux/modules/projects/selectors';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import {
  BUILDING_HEADCOUNT_SEATCOUNT,
  BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS,
  BUILDING_USF,
  FLOOR_HEADCOUNT_SEATCOUNT,
  FLOOR_USF,
} from 'spw-redux/modules/calculators/types';

import { PlainProgramScenario } from '../../models/Scenario';

interface IProps {
  scenario: PlainProgramScenario;
  totalWorkSeats: number;
  totalHeadcount: number;
  circulationPercentage: number;
  planningFactorPercentage: number;
  deltaProgramUSF: number;
  totalProgramUSF: number;
  subtotalProgramUSF: number;
  showDecimals: boolean;
  testingPrefix: string;
}

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  & > div {
    display: flex;
    flex-direction: column;

    &:not(:first-child) {
      margin-left: 24px;
    }

    &:nth-child(2) {
      min-width: 170px;
      display: flex;
      flex-direction: row;
      flex: 1;

      > :nth-child(2) {
        margin-left: 4px;

        .value {
          font-weight: 400;
        }
      }
    }

    &:nth-child(4) {
      width: 102px;
    }
  }
`;

export const WorksheetLabel = ({
  scenario,
  totalWorkSeats,
  totalHeadcount,
  showDecimals,
  testingPrefix,
  circulationPercentage,
  deltaProgramUSF,
  planningFactorPercentage,
  totalProgramUSF,
  subtotalProgramUSF,
}: IProps) => {
  const { getUnit, convertToUnit } = useUnitConversion();
  const project = useSelector(currentProject);
  const primaryLabelLabel = useMemo(() => {
    if (!scenario) return '';

    switch (scenario.calculatorType) {
      case BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS:
        return 'Department Name';

      case FLOOR_HEADCOUNT_SEATCOUNT:
      case FLOOR_USF:
        return 'Floor Name';

      default:
        return 'Program Type';
    }
  }, [scenario]);

  if (!scenario) return null;

  const { calculatorType, department, startYear, targetYear } = scenario;

  let yearPill: JSX.Element | null = null;

  if (
    [
      BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS,
      TOTAL_PROGRAM_TYPE,
      BUILDING_HEADCOUNT_SEATCOUNT,
      FLOOR_HEADCOUNT_SEATCOUNT,
    ].includes(calculatorType)
  ) {
    yearPill = (
      <div className="pill left-align">
        <div className="value" data-testid={`${testingPrefix}_label2`}>
          {targetYear !== startYear
            ? `Year ${targetYear - startYear} (${targetYear})`
            : `Current (${targetYear})`}
        </div>
        <div className="label">Target Year</div>
      </div>
    );
  }

  let secondaryId: JSX.Element | null = null;

  if (
    calculatorType === BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS &&
    department?.secondaryIdentifier
  ) {
    secondaryId = (
      <div>
        <div className="pill">
          <div className="value limited">{department.secondaryIdentifier}</div>
        </div>
      </div>
    );
  }

  let mainLabel = scenario.primaryLabel;
  switch (calculatorType) {
    case MACRO_PROGRAMMING_TYPE:
      mainLabel = 'Macro';
      break;

    case BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS:
      mainLabel = department?.name;
      break;

    case TOTAL_PROGRAM_TYPE:
      mainLabel = 'Multiple Departments';
      break;

    case BUILDING_HEADCOUNT_SEATCOUNT:
    case BUILDING_USF:
      mainLabel = 'Building';
      break;

    case FLOOR_HEADCOUNT_SEATCOUNT:
    case FLOOR_USF:
      mainLabel = project.buildings?.[0].floors.find(
        (f) => f.id === scenario.floorId
      )?.name;
      break;

    default:
      break;
  }

  const formattedDeltaUSF = convertToUnit(deltaProgramUSF, {
    unitType: 'area',
    formatter: (v) => asUsfOrZero(v, showDecimals),
  });

  return (
    <Wrapper>
      <div>
        <div className="pill left-align">
          <div
            className="value"
            data-testid={`${testingPrefix}_total_workseats`}
          >
            {numberWithCommas(totalWorkSeats, showDecimals, true)}
          </div>
          <div
            className="label"
            data-testid={`${testingPrefix}_total_workseats_label`}
          >
            Total Work Seats
          </div>
        </div>
        <div className="pill left-align">
          <div
            className="value"
            data-testid={`${testingPrefix}_total_headcount`}
          >
            {numberWithCommas(totalHeadcount, showDecimals, true)}{' '}
          </div>
          <div
            className="label"
            data-testid={`${testingPrefix}_total_headcount_label`}
          >
            Total Headcount
          </div>
        </div>
      </div>
      <div>
        <div>
          <div className="pill left-align">
            <div
              className={`value ${secondaryId ? 'limited' : ''}`}
              data-testid={`${testingPrefix}_label1`}
            >
              {mainLabel}
            </div>
            <div className="label">{primaryLabelLabel}</div>
          </div>
          {yearPill}
        </div>
        {secondaryId}
      </div>
      <div>
        <div className="pill">
          <div
            className="value"
            data-testid={`${testingPrefix}_circulation_percentage`}
          >
            {numberWithCommas(circulationPercentage, showDecimals, true)}%
          </div>
          <div
            className="label"
            data-testid={`${testingPrefix}_circulation_percentage_label`}
          >
            Circulation
          </div>
        </div>
        <div className="pill">
          <div
            className="value"
            data-testid={`${testingPrefix}_planning_factor_percentage`}
          >
            {numberWithCommas(planningFactorPercentage, showDecimals, true)}%
          </div>
          <div
            className="label"
            data-testid={`${testingPrefix}_planning_factor_percentage_label`}
          >
            Planning Factor
          </div>
        </div>
      </div>
      <div>
        {calculatorType !== MACRO_PROGRAMMING_TYPE && (
          <div className="pill">
            <div
              className={`value ${deltaProgramUSF > 0 ? 'positive' : ''} ${
                deltaProgramUSF < 0 ? 'negative' : ''
              }`}
              data-testid={`${testingPrefix}_program_delta_usf`}
            >
              {deltaProgramUSF > 0
                ? `+${formattedDeltaUSF}`
                : `${formattedDeltaUSF}`}
            </div>
            <div
              className="label"
              data-testid={`${testingPrefix}_program_delta_usf_label`}
            >
              Program Delta {getUnit('area')}
            </div>
          </div>
        )}
      </div>
      <div>
        <div>
          <div className="pill">
            <div className="value" data-testid="worksheet_header_total_usf">
              {convertToUnit(totalProgramUSF, {
                unitType: 'area',
                formatter: (v) => numberWithCommas(v, showDecimals, true),
              })}
            </div>
            <div
              className="label"
              data-testid="worksheet_header_total_usf_label"
            >
              Total Program {getUnit('area')}
            </div>
          </div>
          <div className="pill">
            <div
              className="value"
              data-testid="worksheet_header_program_subtotal_usf"
            >
              {convertToUnit(subtotalProgramUSF, {
                unitType: 'area',
                formatter: (v) => numberWithCommas(v, showDecimals, true),
              })}
            </div>
            <div
              className="label"
              data-testid="worksheet_header_program_subtotal_usf_label"
            >
              Subtotal Program {getUnit('area')}
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};
